import styled, {css} from 'styled-components';

import {color, typography} from '../../../shared/styles';

import {
  IconPosition,
  InputAlignProp,
  InputStyleProp,
  InputTypeProp,
} from './types';

interface ContainerProps {
  $fitParent: boolean;
  $size: InputStyleProp;
  $colorOnFocus: string;
  $disabled?: boolean;
}

interface IconInputContainerProps {
  $hasIcon: boolean;
  $iconPosition: IconPosition;
  $size: InputStyleProp;
  $textAlign: InputAlignProp;
  $inputType: InputTypeProp;
  $colorOnFocus: string;
  $hasError: boolean;
  $disabled?: boolean;
}

export const COMP_NAME = 'ZUIInput';

export const Container = styled.div<ContainerProps>`
  position: relative;
  margin: 8px 0;
  font-family: ${typography.type.primary};
  color: ${color.dark};
  outline: none;

  ${(props) => props.$size === 'big' && 'width: 280px;'}
  ${(props) => props.$size === 'medium' && 'width: 343px;'}
  ${(props) => props.$size === 'normal' && 'width: 220px;'}
  ${(props) => props.$fitParent && 'width: 100%;'}

  label {
    display: block;
    margin: 0 0 6px;
    width: fit-content;
    color: ${({$colorOnFocus}) => $colorOnFocus};

    &.${COMP_NAME}--fit-parent {
      width: 100%;
    }
    ${({$disabled}) =>
      $disabled &&
      css`
        color: ${color.medium};
      `}
  }

  input {
    ${({$disabled}) =>
      $disabled &&
      css`
        color: ${color.medium};
      `}
  }

  .${COMP_NAME}__icon-search {
    padding: 0;
    border: none;
    background: none;
    color: inherit;
    font: inherit;

    ${({$disabled}) =>
      $disabled &&
      css`
        img,
        i {
          cursor: not-allowed;
          filter: brightness(0) saturate(100%) invert(75%) sepia(8%)
            saturate(5%) hue-rotate(325deg) brightness(95%) contrast(90%);
        }
      `}
  }

  .${COMP_NAME} {
    &__hint-container {
      display: flex;
      align-items: center;
      margin-top: 5px;
      position: relative;

      &_optional-message {
        position: absolute;
        right: 0;
        top: 0;
        z-index: 9;
        font-family: ${typography.type.primary};
        font-style: normal;
        font-weight: ${typography.weight.regular};
        font-size: ${typography.size.s1};
        line-height: 14px;
        text-align: right;
        color: var(--text-default);
      }

      &_hint {
        font-family: ${typography.type.primary};
        font-style: normal;
        font-weight: ${typography.weight.regular};
        font-size: ${typography.size.s1};
        line-height: 14px;
        color: ${color.mediumDark};
      }

      &_error {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        z-index: 9;
        font-family: ${typography.type.primary};
        font-style: normal;
        font-weight: ${typography.weight.regular};
        font-size: ${typography.size.s1};
        line-height: 14px;
        text-align: left;
        color: ${color.red};
        box-sizing: border-box;
        background-color: #ffffff;
      }
    }

    &__loader {
      position: absolute;
      height: 100%;
      width: 24px;
      display: flex;
      align-items: center;
      right: 0;
      margin-right: 16px;
    }
  }
`;

export const InputWrapper = styled.div<IconInputContainerProps>`
  display: flex;
  flex-direction: ${(props) =>
    props.$hasIcon && props.$iconPosition === 'left' ? 'row-reverse' : 'row'};
  width: 100%;
  padding-right: 0;
  background-color: ${({$disabled}) =>
    $disabled ? color.mediumLight : `#ffffff`};
  border: 1px solid
    ${({$hasError}) => ($hasError ? color.red : color.mediumLight)};
  border-radius: 4px;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;

  ${(props) => props.$size === 'big' && 'min-height: 72px;'}
  ${(props) => props.$size === 'medium' && 'min-height: 56px;'}
  ${(props) => props.$size === 'normal' && 'min-height: 46px;'}

  &:focus-within {
    border: 2px solid
      ${({$hasError}) =>
        $hasError ? 'var(--border-error)' : 'var(--text-default)'};
  }

  input {
    ${(props) =>
      props.$hasIcon && props.$iconPosition === 'right'
        ? 'padding-right: 0;'
        : ''}

    ${(props) =>
      props.$hasIcon && props.$iconPosition === 'left'
        ? 'padding-left: 0;'
        : ''}

    ${(props) => props.$size === 'big' && 'font-size: 24px; padding: 0 32px;'}

    ${(props) =>
      props.$size === 'medium' && 'font-size: 24px; padding: 0 32px;'}

    ${(props) =>
      props.$inputType === 'zip' &&
      props.$size !== 'normal' &&
      'letter-spacing: 24px; text-indent: 12px;'}

    text-align: ${(props) => props.$textAlign};
  }

  &.${COMP_NAME}--invalid {
    color: var(--border-error);
    border-color: var(--border-error);

    &.zactive {
      border-width: 2px;
    }
  }

  .${COMP_NAME}__icon-search, .${COMP_NAME}__show_password {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    min-width: 20px;
    padding: ${(props) =>
      props.$iconPosition === 'left' ? '0 8px 0 15px' : '0 15px 0 8px'};
    height: initial;
    cursor: pointer;

    img {
      width: 20px;
    }
  }

  .${COMP_NAME}__show_password {
    padding: 0;
    border: none;
    background: none;
    justify-content: center;
    flex-shrink: 0;
    font-size: ${typography.size.s1};
  }

  .${COMP_NAME}__is-ready {
    display: flex;
    width: ${(props) => (props.$size === 'big' ? '22px' : '15px')};
    padding: 5px;
    align-items: center;
    justify-content: center;

    i {
      width: 100%;
      font-size: var(--size-small);
      color: var(--text-success);
    }
  }
`;

export const StyledInput = styled.input<{disabled?: boolean}>`
  width: 100%;
  min-width: 0;
  height: auto;
  padding: 0 15px;
  font-size: ${typography.size.s3};
  font-family: ${typography.type.primary};
  color: ${({disabled}) => (disabled ? color.medium : color.dark)};
  background-color: #ffffff;
  background-size: 20px;
  background-repeat: no-repeat;
  background-position: right 15px bottom 13px;
  border: none;
  transition: all 0.3s ease-in-out;
  -webkit-appearance: none;
  box-sizing: border-box;
  outline: none;

  /* Disable type=number arrows */
  &[type='number'] {
    -webkit-appearance: textfield;
    -moz-appearance: textfield;
    appearance: textfield;
  }

  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
  }

  ::placeholder {
    color: ${color.mediumDark};
    ${({disabled}) =>
      disabled &&
      css`
        color: ${color.medium};
      `}
  }

  &:focus {
    outline: none;
  }

  &.${COMP_NAME}--loading {
    background-image: radial-gradient(
      var(--surface-brand) 15%,
      var(--surface-brand) 16%
    );
    background-size: 0% 2px;
    background-position: 0 100%;
    background-repeat: no-repeat;
    -webkit-animation: animatedBackground 0.8s linear infinite alternate;
    animation: animatedBackground 0.8s linear infinite alternate;
  }

  @keyframes animatedBackground {
    0% {
      background-position: 0 100%;
      background-size: 0% 2px;
    }

    50% {
      background-position: 0 100%;
      background-size: 100% 2px;
    }

    51% {
      background-position: 100% 100%;
      background-size: 100% 2px;
    }

    100% {
      background-position: 100% 100%;
      background-size: 0% 2px;
    }
  }

  ${({disabled}) =>
    disabled &&
    css`
      background: ${color.mediumLight};
      cursor: not-allowed;
    `}
`;

export const Loader = styled.div`
  position: absolute;
  width: 24px;
  height: 24px;
  animation: spCircRot 0.8s infinite linear;
  box-sizing: border-box;
  border: 2px ${color.dark} solid;
  border-top: 2px rgba(255, 255, 255, 1) solid;
  border-radius: 50%;

  &.blue {
    border-color: var(--surface-brand);
    border-top: 2px rgba(255, 255, 255, 1) solid;
  }

  &.gray {
    border-color: ${color.medium};
    border-top: 2px rgba(255, 255, 255, 1) solid;
  }

  @-webkit-keyframes spCircRot {
    from {
      -webkit-transform: rotate(0deg);
    }

    to {
      -webkit-transform: rotate(359deg);
    }
  }

  @keyframes spCircRot {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(359deg);
    }
  }
`;
